import angular from 'angular'

import { compact, uniqueId, difference } from 'lodash-es'

import setCss from '../utils/set-css'
import getSize from '../utils/get-size'
import StrictObj from '../utils/strict-obj'
import extractRawElm from '../utils/extract-raw-elm'
import viewportResize from '../utils/viewport-resize'

import { TRANSFORM, fmtTranslateX } from '../utils/fmt-translate'

angular
  .module('ggs.gallery-modal-list')
  .factory('GmlView', GmlViewProvider)

const ITEM_QS = '.ggs-gml__item'
const CONTENT_QS = 'ggs-gallery-modal'
const SHOWCASE_QS = '.ggs-gml__showcase'

const MAIN_SHOW = 'ggs-gml__main_show'
const MAIN_DISPLAY = 'ggs-gml__main_display'

const ITEM_SHOW = 'ggs-gml__item_show'
const ITEM_ANIMATE = 'ggs-gml__item_animate'
const ITEM_CURRENT = 'ggs-gml__item_current'

const ITEM_PLACED_FOR_ROLE = 'ggs-placed-for-role'
const ITEM_PLACED_FOR_POST = 'ggs-placed-for-post'
const ITEM_PLACED_FOR_CONTENT = 'ggs-placed-for-content'
const ITEM_PLACED_FOR_VIEWPORT = 'ggs-placed-for-viewport'

const ITEM_TIMEOUT = 1e3
const MAIN_TIMEOUT = 1e3

const ROLE = StrictObj({
  PREV: uniqueId('prev'),
  CURR: uniqueId('curr'),
  NEXT: uniqueId('next')
})

function GmlViewProvider(
  qh,
  domh,
  waitAngular,
  DefineMethod,
  htmlOverflowY,
  WaitTransitionEnd) {

  return function GmlView(
    $listElm,
    properties,
    postHash,
    post2id,
    reportPopupOpen,
    reportPopupClosed) {

    const sign = `galleryList::gmlView[uid=${uniqueId()}]`
    const listElm = extractRawElm($listElm)
    const showcaseElm = domh.find($listElm, SHOWCASE_QS)

    const gmlView = StrictObj({ wrapM: DefineMethod(sign) })

    gmlView.wrapM('open',
      qh.makeOver(
        hideScrollbar,
        cleanItems,
        placeItems,
        displayMain,
        waitAngular,
        reportPopupOpen,
        showMain,
        waitMain))

    gmlView.wrapM('close',
      qh.makeOver(
        placeItems,
        notShowMain,
        reportPopupClosed,
        waitMain,
        cleanItems,
        notDisplayMain,
        notHideScrollbar))

    gmlView.wrapM('switch',
      qh.makeOver(
        placeItems,
        waitCurr))

    gmlView.wrapM('refresh', placeItems)

    gmlView.wrapM('destroy', notHideScrollbar)

    return gmlView

    // open
    function hideScrollbar() {
      htmlOverflowY.hidden()
    }

    function cleanItems() {
      const allItems = getAllItems()

      for (const itemElm of allItems) {
        cleanItem(itemElm)
      }
    }

    function displayMain() {
      listElm.classList.add(MAIN_DISPLAY)
    }

    function showMain() {
      listElm.classList.add(MAIN_SHOW)
    }

    // close
    function notShowMain() {
      listElm.classList.remove(MAIN_SHOW)
    }

    function notHideScrollbar() {
      htmlOverflowY.auto()
    }

    function notDisplayMain() {
      listElm.classList.remove(MAIN_DISPLAY)
    }

    // prev\next
    function waitCurr() {
      if (!properties.modals_use_continuous_switching) {
        const items = getItems()
        return waitItem(items.currElm)
      }
    }

    // refresh
    function placeItems() {
      const items = getItems()
      const viewportWidth = viewportResize.getWidth()

      if (items.currElm) {
        const contentWidth = getContentWidth(items.currElm)
        if (!isPlaced(items.currElm, ROLE.CURR, postHash.currPost.id, contentWidth, viewportWidth)) {
          const itemPos = calcCurrItemPos(contentWidth, viewportWidth)
          placeItem(items.currElm, itemPos, ROLE.CURR, postHash.currPost.id, contentWidth, viewportWidth)
        }
      }

      if (items.prevElm) {
        const contentWidth = getContentWidth(items.prevElm)
        if (!isPlaced(items.prevElm, ROLE.PREV, postHash.prevPost.id, contentWidth, viewportWidth)) {
          const itemPos = calcPrevItemPos(contentWidth, viewportWidth)
          placeItem(items.prevElm, itemPos, ROLE.PREV, postHash.prevPost.id, contentWidth, viewportWidth)
        }
      }

      if (items.nextElm) {
        const contentWidth = getContentWidth(items.nextElm)
        if (!isPlaced(items.nextElm, ROLE.NEXT, postHash.nextPost.id, contentWidth, viewportWidth)) {
          const itemPos = calcNextItemPos(contentWidth, viewportWidth)
          placeItem(items.nextElm, itemPos, ROLE.NEXT, postHash.nextPost.id, contentWidth, viewportWidth)
        }
      }

      const allItems = getAllItems()
      const freeItems = difference(allItems, items.attachedElms)

      for (const freeItem of freeItems) {
        cleanItem(freeItem)
      }
    }

    function cleanItem(itemElm) {
      setCss(itemElm, TRANSFORM, null)
      itemElm.classList.remove(ITEM_SHOW)
      itemElm.classList.remove(ITEM_ANIMATE)
      itemElm.classList.remove(ITEM_CURRENT)
      itemElm.removeAttribute(ITEM_PLACED_FOR_ROLE)
      itemElm.removeAttribute(ITEM_PLACED_FOR_POST)
      itemElm.removeAttribute(ITEM_PLACED_FOR_CONTENT)
      itemElm.removeAttribute(ITEM_PLACED_FOR_VIEWPORT)
    }

    function isPlaced(itemElm, itemRole, postId, contentWidth, viewportWidth) {
      return true
        && (itemRole === itemElm.getAttribute(ITEM_PLACED_FOR_ROLE))
        && (postId === parseInt(itemElm.getAttribute(ITEM_PLACED_FOR_POST)))
        && (contentWidth === parseInt(itemElm.getAttribute(ITEM_PLACED_FOR_CONTENT)))
        && (viewportWidth === parseInt(itemElm.getAttribute(ITEM_PLACED_FOR_VIEWPORT)))
    }

    function placeItem(itemElm, itemPos, itemRole, postId, contentWidth, viewportWidth) {
      const currRole = itemRole === ROLE.CURR
      const sameRole = isSameRole(itemElm, itemRole)
      const samePost = isSamePost(itemElm, postId)

      const curr = currRole
      const show = currRole || (samePost && !sameRole)
      const anim = currRole || (samePost && !sameRole)

      show
        ? itemElm.classList.add(ITEM_SHOW)
        : itemElm.classList.remove(ITEM_SHOW)

      curr
        ? itemElm.classList.add(ITEM_CURRENT)
        : itemElm.classList.remove(ITEM_CURRENT)

      anim
        ? itemElm.classList.add(ITEM_ANIMATE)
        : itemElm.classList.remove(ITEM_ANIMATE)

      setCss(itemElm, TRANSFORM, fmtTranslateX(itemPos))
      itemElm.setAttribute(ITEM_PLACED_FOR_ROLE, itemRole)
      itemElm.setAttribute(ITEM_PLACED_FOR_POST, postId)
      itemElm.setAttribute(ITEM_PLACED_FOR_CONTENT, contentWidth)
      itemElm.setAttribute(ITEM_PLACED_FOR_VIEWPORT, viewportWidth)
    }

    function isSamePost(itemElm, postId) {
      return parseInt(itemElm.getAttribute(ITEM_PLACED_FOR_POST)) === postId
    }

    function isSameRole(itemElm, itemRole) {
      return itemElm.getAttribute(ITEM_PLACED_FOR_ROLE) === itemRole
    }

    // calc
    function calcCurrItemPos(_contentWidth, _viewportWidth) {
      return 0
    }

    function calcNextItemPos(contentWidth, viewportWidth) {
      const itemPos = Math.round(viewportWidth / 2 + contentWidth / 2) + 10
      return itemPos
    }

    function calcPrevItemPos(contentWidth, viewportWidth) {
      const itemPos = -1 * Math.round(viewportWidth / 2 + contentWidth / 2) - 10
      return itemPos
    }

    function getContentWidth(itemElm) {
      const contentElm = domh.find(itemElm, CONTENT_QS)
      const contentWidth = getSize(contentElm).width
      return contentWidth
    }

    // getItems

    function getItems() {
      const {
        prevPost,
        currPost,
        nextPost } = postHash

      const prevElm = prevPost && domh.find.byId.notStrict(post2id(prevPost))
      const currElm = currPost && domh.find.byId.notStrict(post2id(currPost))
      const nextElm = nextPost && domh.find.byId.notStrict(post2id(nextPost))

      return StrictObj({
        prevElm,
        currElm,
        nextElm,
        attachedElms: compact([
          prevElm,
          currElm,
          nextElm
        ])
      })
    }

    function getAllItems() {
      return domh.find.all.notStrict(listElm, ITEM_QS)
    }

    // transitions
    function waitMain() {
      return WaitTransitionEnd(MAIN_TIMEOUT, showcaseElm, 'transform')
    }

    function waitItem(itemElm) {
      return WaitTransitionEnd(ITEM_TIMEOUT, itemElm, 'transform')
    }
  }
}
